import { ReactNode, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell, BrandMark, Button, Inline, Kbd, Stack, StatusDot, t, Text } from '@kanbios/ui';
import { api, unwrap } from '../lib/api';
import { shortDate } from '../lib/format';
import { useSession, useSignOut } from '../lib/session';
import { EnvironmentPicker } from './EnvironmentPicker';
import { SearchPalette } from './SearchPalette';
import { useNarrow } from '../lib/useNarrow';

export interface Crumb {
  label: ReactNode;
  href?: string;
}

/** D107: the palette's shortcut as the keyboard says it (⌘K on a Mac, Ctrl K elsewhere). */
const SHORTCUT = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';
const GROUP_ORDER = [null, 'inventory', 'operations', 'security', 'project'] as const;


/**
 * Signed-in frame: DSM AppShell with the menu by object (SPEC-002). Entries
 * come from the backend: an entry appears with its module, never as a dead link.
 * Sign-out sits in the sidebar footer and revokes the session (ADR-012).
 */
export function Shell({ crumbs, header, footer, children }: { crumbs: Crumb[]; header?: ReactNode; footer?: ReactNode; children?: ReactNode }) {
  const { me } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const queryClient = useQueryClient();
  const [searching, setSearching] = useState(false);
  const narrow = useNarrow();
  const navigation = useQuery({ queryKey: ['navigation'], queryFn: async () => unwrap(await api.maintenance.GET('/navigation')), staleTime: 5 * 60_000 });
  const items = navigation.data?.items ?? [];
  const active = items.find(i => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`))?.id;
  const groups = GROUP_ORDER.map(group => ({
    label: group ? t(`nav.groups.${group}`) : undefined,
    items: items.filter(i => i.group === group).map(i => ({ id: i.id, label: t(i.labelKey), icon: i.icon })),
  })).filter(g => g.items.length > 0);

  // Crumbs keep a real href (open in new tab) and navigate client-side on click.
  const routedCrumbs = crumbs.map(c => (c.href ? {
    ...c,
    onClick: (e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      navigate(c.href!);
    },
  } : c));

  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const toggleTheme = async () => {
    await api.maintenance.PUT('/preferences', { body: { theme: dark ? 'light' : 'dark' } });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  return (
    <AppShell
      // Full width: dense tables and the server page use the whole screen.
      maxWidth="none"
      header={header}
      footer={footer}
      sidebar={{
        label: t('nav.label'),
        brand: <BrandMark name={t('app.brand')} />,
        groups,
        active,
        onNavigate: (id: string) => {
          const item = items.find(i => i.id === id);
          if (item) navigate(item.path);
        },
        user: { initials: me.user.initials, name: me.user.name, role: me.roles.map(r => t(`roles.${r}`)).join(', ') },
        onSignOut: signOut,
        // D75: Ansible, Checkmk and the version above « Replier » (DSM Sidebar footer; hidden when collapsed).
        footer: <PlatformParts />,
      }}
      topbar={{
        crumbs: routedCrumbs, breadcrumbLabel: t('shell.breadcrumb'), notifications: false, dark, onToggleTheme: toggleTheme,
        // D107: no search box; its keyboard shortcut, beside the theme toggle, opens the palette (⌘K / Ctrl K).
        search: false,
        // Phone width: the mention does not fit the bar; the server page carries « Appeler l'astreinte » (SPEC-004).
        // D75: the environment filter right before the theme toggle; the platform's parts moved to the sidebar.
        extra: (
          <Inline gap={16} align="center">
            {!narrow && <OnCallMention />}
            <EnvironmentPicker />
            {/* WCAG 2.5.3: the button's name starts with what it shows (the shortcut), then says what it does. */}
            <Button variant="ghost" size="sm" aria-label={t('search.buttonShortcut', { shortcut: SHORTCUT })} onClick={() => setSearching(true)}><Kbd>{SHORTCUT}</Kbd></Button>
          </Inline>
        ),
      }}
    >
      {children ?? <Outlet />}
      <SearchPalette open={searching} onOpen={() => setSearching(true)} onClose={() => setSearching(false)} />
    </AppShell>
  );
}

/** SPEC-009: « Astreinte : <nom> jusqu'au <date> » in the TopBar, for every role. */
function OnCallMention() {
  const turn = useQuery({
    queryKey: ['oncall-current'],
    queryFn: async () => unwrap(await api.maintenance.GET('/notifications/oncall/current')),
    staleTime: 10 * 60_000,
    retry: false,
  }).data;
  if (!turn?.level1) return null;
  const until = shortDate(turn.until);
  return <Text variant="body-sm" tone="mute">{t('shell.onCall', { name: turn.level1.name, until })}</Text>;
}

/** D41: the platform's parts and the version, read every 30 s. */
function usePlatform() {
  return useQuery({
    queryKey: ['platform'],
    queryFn: async () => unwrap(await api.provisioning.GET('/platform')),
    refetchInterval: 30_000,
    retry: false,
  }).data;
}

const PART_TONES = { ok: 'pos', failing: 'neg', not_configured: 'neutral' } as const;

/** D41: « Ansible » and « Checkmk », each a dot (the tooltip says its version or its error), then the version; D75: in the sidebar. */
function PlatformParts() {
  const platform = usePlatform();
  if (!platform) return null;
  const parts = [['engine', platform.engine], ['checkmk', platform.checkmk]] as const;
  // On the sidebar's shell ground: plain text, which takes the sidebar's own colour (a mute tone would lose its contrast there).
  return (
    <Stack gap={8} aria-label={t('shell.platform.label')}>
      <Inline gap={12} align="center">
        {parts.map(([id, part]) => part && (
          <Inline key={id} gap={4} align="center">
            <StatusDot
              tone={PART_TONES[part.state]}
              variant={part.state === 'not_configured' ? 'ring' : 'solid'}
              label={t(`shell.platform.${id}`)}
              description={part.state === 'ok'
                ? t('shell.platform.ok', { version: part.version ?? '—' })
                : part.state === 'failing' ? t('shell.platform.failing', { error: part.error ?? '' }) : t('shell.platform.notConfigured')}
            />
            <span>{t(`shell.platform.${id}`)}</span>
          </Inline>
        ))}
      </Inline>
      {/* The version deployed (the release tag). */}
      <span>{t('shell.platform.version', { version: platform.version })}</span>
    </Stack>
  );
}
